/**
 * The learner's badges: a cached list (shown at once in the profile) plus the server check
 * that awards new ones. Newly earned badges are announced to the unlock overlay.
 */
import { checkAchievements, fetchMyAchievements, type EarnedAchievement } from './api.ts';
import { readJson, writeJson, writeString } from './storage.ts';

export const ACHIEVEMENTS_CACHE_KEY = 'soile.achievements.v1';

let mine: EarnedAchievement[] | null = null;
let checking: Promise<void> | null = null;
const listListeners = new Set<(list: EarnedAchievement[]) => void>();
const unlockListeners = new Set<(codes: string[]) => void>();

export function cachedAchievements(): EarnedAchievement[] {
  mine ??= readJson<EarnedAchievement[]>(ACHIEVEMENTS_CACHE_KEY) ?? [];
  return mine;
}

function store(list: EarnedAchievement[]): void {
  mine = list;
  writeJson(ACHIEVEMENTS_CACHE_KEY, list);
  listListeners.forEach((l) => l(list));
}

/** Refreshes the cached list from the server (profile screen). */
export async function refreshAchievements(): Promise<EarnedAchievement[]> {
  const list = await fetchMyAchievements();
  store(list);
  return list;
}

/**
 * Asks the server to award anything earned (after a chapter or a review session). One
 * check runs at a time; failures are silent — the next check catches up.
 */
export function runAchievementCheck(): Promise<void> {
  checking ??= checkAchievements()
    .then((fresh) => {
      if (fresh.length === 0) return;
      const known = new Set(cachedAchievements().map((a) => `${a.code}|${a.period}`));
      const added = fresh.filter((a) => !known.has(`${a.code}|${a.period}`));
      if (added.length === 0) return;
      store([...cachedAchievements(), ...added]);
      const codes = added.map((a) => a.code);
      unlockListeners.forEach((l) => l(codes));
    })
    .catch(() => undefined)
    .finally(() => {
      checking = null;
    });
  return checking;
}

export function subscribeAchievements(listener: (list: EarnedAchievement[]) => void): () => void {
  listListeners.add(listener);
  return () => listListeners.delete(listener);
}

export function subscribeUnlocks(listener: (codes: string[]) => void): () => void {
  unlockListeners.add(listener);
  return () => unlockListeners.delete(listener);
}

/** Sign-out: the next person must not see these badges. */
export function clearAchievements(): void {
  mine = null;
  writeString(ACHIEVEMENTS_CACHE_KEY, null);
}
